import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../utils/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getCurrentMember, isMemberAuthenticated, logoutMember } from '../api/memberAuthService';
import { syncPushToken, onForegroundMessage } from '../api/fcmService';

const MemberAuthContext = createContext();

export function useMemberAuth() {
  return useContext(MemberAuthContext);
}

export function MemberAuthProvider({ children }) {
  const [currentMember, setCurrentMember] = useState(null);
  const [loading, setLoading] = useState(true);

  const isMemberPath = (pathname) => pathname.includes('/member/');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) setLoading(true);
      try {
        if (user) {
          const isMem = await isMemberAuthenticated();
          if (isMem) {
            const member = await getCurrentMember();
            setCurrentMember(member);
            // Re-capture this browser's latest push token on every sign-in. Silent: it never shows the
            // permission dialog, so it only does anything once the member has already allowed notifications.
            if (member?.subscriptionPreferences?.pushNotifications !== false) {
              syncPushToken('member', { prompt: false });
            }
          } else {
            if (isMemberPath(window.location.pathname)) setCurrentMember(null);
          }
        } else {
          if (isMemberPath(window.location.pathname)) setCurrentMember(null);
        }
      } catch (err) {
        console.error('MemberAuthContext error:', err);
        if (isMemberPath(window.location.pathname)) setCurrentMember(null);
      } finally {
        setLoading(false);
      }
    });
    const stopForeground = onForegroundMessage();
    return () => { unsubscribe(); stopForeground(); };
  }, []);

  const handleLogout = async () => {
    try {
      setCurrentMember(null);
      await logoutMember();
      return true;
    } catch (err) {
      throw err;
    }
  };

  const value = { currentMember, loading, logout: handleLogout };

  return (
    <MemberAuthContext.Provider value={value}>
      {children}
    </MemberAuthContext.Provider>
  );
}
